import './App.css';
import { useRecoilValue } from 'recoil';
import { authStateAtom } from './models/atoms/auth_atoms';
import { type NavState, navStateAtom } from './models/atoms/ui_atoms';
import { memo, useEffect } from 'react';
import {
  userAccountAtom,
  accountCompanyInfoAtom,
} from './models/atoms/account_atoms';
import { PostHogProvider, usePostHog } from 'posthog-js/react';
import { auth_controller } from './controllers/auth_controller';
import { account_controller } from './controllers/account_controller';
import { company_controller } from './controllers/company_controller';
import { LoginPage } from './views/pages/login';
import { DeviceMetricsPage } from './views/pages/device_metrics';
import { AccountsPage } from './views/pages/accounts';
import { SettingsPage } from './views/pages/settings';
import {
  TranslationProvider,
  useTranslation,
} from './contexts/TranslationContext'; // Ensure this is imported
import FirstLoginModal from './views/components/FirstLoginModal';
import { BatteryLife } from './views/components/BatteryLife';
import { PermissionsModalContextProvider } from './contexts/PermissionModalContext';
import { SnackbarProvider } from 'notistack';
import { Box, Stack } from '@mui/material';
import { MHMThemeProvider } from './contexts/MHMThemeProvider';
import { Navbar, NAVBAR_HEIGHT } from './views/components/navbar/Navbar';
import { Footer, FOOTER_HEIGHT } from './views/components/Footer';
import { useIsMobile } from './hooks/useIsMobile';
import { MsalProvider } from '@azure/msal-react';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';

export const MainPage = memo(function MainPage({ onLogout }: AppProps) {
  const navState = useRecoilValue<NavState>(navStateAtom);
  const { setDefaultLanguage } = useTranslation();

  const isBatteryCalculatorPage = window.location.pathname === '/battery_life';

  const isMobile = useIsMobile();

  useEffect(() => {
    if (isBatteryCalculatorPage) {
      return;
    }

    account_controller.get_company_info();
    account_controller.get_company_auth_settings();
    account_controller.get_account_list();
    account_controller.get_user_account(setDefaultLanguage);
    company_controller.setup();
  }, [isBatteryCalculatorPage, setDefaultLanguage]);

  const userAccount = useRecoilValue(userAccountAtom);
  const companyInfo = useRecoilValue(accountCompanyInfoAtom);
  const posthog = usePostHog();

  useEffect(() => {
    if (userAccount && posthog) {
      posthog.identify(userAccount.id, {
        email: userAccount.email,
        name: `${userAccount.first_name} ${userAccount.last_name}`,
      });

      if (userAccount.company_id) {
        posthog.group(
          'company',
          userAccount.company_id,
          companyInfo ? { name: companyInfo.name } : {},
        );
      }
    }
  }, [userAccount, companyInfo, posthog]);

  let page = <></>;

  console.log(window.location);
  if (isBatteryCalculatorPage) {
    return (
      <div
        style={{
          display: 'flex',
          height: '100vh',
          justifyContent: 'center',
          alignItems: 'center',
          backgroundColor: '#e3eefa',
        }}
      >
        <BatteryLife />
      </div>
    );
  }

  switch (navState) {
    case 'device-metrics':
      page = <DeviceMetricsPage style={{ height: '100%' }}></DeviceMetricsPage>;
      break;
    case 'accounts-page':
      page = <AccountsPage style={{ height: '100%' }}></AccountsPage>;
      break;
    case 'settings-page':
      page = <SettingsPage style={{ height: '100%' }}></SettingsPage>;
      break;
  }

  return (
    <Stack height="100vh" overflow="hidden">
      <Navbar onLogout={onLogout} />

      <Box
        flex={1}
        pt={`${NAVBAR_HEIGHT}px`}
        pb={isMobile ? 0 : `${FOOTER_HEIGHT}px`}
        overflow="hidden"
      >
        {page}
      </Box>

      <FirstLoginModal />

      {!isMobile && <Footer />}
    </Stack>
  );
});

const FilterStateComponent = memo(function FilterStateComponent({
  onLogout,
}: AppProps) {
  return <MainPage onLogout={onLogout} />;
});

type AppProps = {
  onLogout: () => void;
};

const AppLocalizationProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const { language } = useTranslation();
  return (
    <LocalizationProvider
      dateAdapter={AdapterDayjs}
      adapterLocale={language.toLowerCase()}
    >
      {children}
    </LocalizationProvider>
  );
};

function App({ onLogout }: AppProps) {
  const auth = useRecoilValue(authStateAtom);
  if (!auth.validToken) {
    auth_controller.check_token();
  }

  return (
    <PostHogProvider
      apiKey={import.meta.env.REACT_APP_POSTHOG_KEY}
      options={{
        api_host: import.meta.env.REACT_APP_POSTHOG_HOST,
        defaults: '2025-05-24',
        capture_exceptions: true,
        debug: import.meta.env.MODE === 'development',
      }}
    >
      <MsalProvider instance={auth_controller.msalInstance}>
        <MHMThemeProvider>
          <TranslationProvider>
            <AppLocalizationProvider>
              <SnackbarProvider>
                <Box
                  sx={{
                    height: '100vh',
                    width: '100vw',
                    bgcolor: 'background.default',
                    overflow: 'hidden',
                  }}
                >
                  {/* TODO: refactor quick & dirty fix for battery_life */}
                  {!auth.validToken &&
                  window.location.pathname !== '/battery_life' ? (
                    <LoginPage />
                  ) : (
                    <PermissionsModalContextProvider>
                      <FilterStateComponent onLogout={onLogout} />
                    </PermissionsModalContextProvider>
                  )}
                </Box>
              </SnackbarProvider>
            </AppLocalizationProvider>
          </TranslationProvider>
        </MHMThemeProvider>
      </MsalProvider>
    </PostHogProvider>
  );
}

export default App;
